import Axios, { VirtualDeviceApi, DeviceApi } from '@/api';

/** VirtualDevice.mappingType: 커튼에 연동된 조명 목록 */
export const CURTAIN_LINKED_LIGHTS_TYPE = 2;

/**
 * UI 노출 여부. 닫힘→ON 후 취침 시 소등 경로가 정해지기 전까지 숨김.
 * (백엔드·페이지 로직은 유지, 모달 진입점만 비활성)
 */
export const SHOW_CURTAIN_LINKED_LIGHTS_UI = false;

const normalizeItems = (items) => {
  if (items == null) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'object') return Object.values(items);
  return [];
};

const toScalar = (raw, fallback = 0) => {
  if (Array.isArray(raw)) return toScalar(raw[0], fallback);
  if (raw === undefined || raw === null || raw === '') return fallback;
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
};

const findDeviceInZones = (zones, deviceId) => {
  const sid = String(deviceId);
  for (const zone of zones || []) {
    for (const g of zone.groups || []) {
      const d = (g.devices || []).find((x) => String(x._id) === sid);
      if (d) return { device: d, groupId: g._id };
    }
  }
  return null;
};

/**
 * 커튼 연동 조명 ON/OFF (앱 열기/닫기용).
 * 밝기·색온도는 각 조명의 현재 설정값을 유지한다.
 */
export const syncCurtainLinkedLights = async ({
  mqttControl,
  store,
  curtainId,
  turnOn,
}) => {
  if (curtainId == null || !mqttControl || !store) return { synced: 0 };
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', curtainId),
    );
    if (result.resultCode === '9001') return { synced: 0 };
    if (result.resultCode !== '0000') return { synced: 0 };
    if (Number(result.resultData?.mappingType) !== CURTAIN_LINKED_LIGHTS_TYPE) {
      return { synced: 0 };
    }

    const memberIds = normalizeItems(result.resultData?.items)
      .filter((it) => Number(it?.type) === 1 && it?._id != null)
      .map((it) => it._id);
    if (memberIds.length === 0) return { synced: 0 };

    const zones = store.getters['home/getZones'] || [];
    const isOn = turnOn ? 1 : 0;
    const byController = {};
    const updates = [];

    for (const mid of memberIds) {
      const found = findDeviceInZones(zones, mid);
      if (!found?.device || found.device.type !== 1 || found.device.isVirtual) continue;
      const d = found.device;
      const cid = d.controllerId;
      if (!byController[cid]) byController[cid] = [];
      byController[cid].push(d);
      updates.push({ groupId: found.groupId, id: d._id, bright: d.item?.bright, color: d.item?.color });
    }

    for (const controllerId of Object.keys(byController)) {
      const group = byController[controllerId];
      const portsArr = [];
      const onoffArr = [];
      const brightArr = [];
      const colorArr = [];
      const deviceIds = [];
      for (const d of group) {
        deviceIds.push(d._id);
        const ports = Array.isArray(d.item?.ports)
          ? d.item.ports
          : (d.item?.ports != null ? [d.item.ports] : []);
        const bright = toScalar(d.item?.bright, 50);
        const color = toScalar(d.item?.color, 10);
        for (const p of ports) {
          portsArr.push(p);
          onoffArr.push(isOn);
          brightArr.push(bright);
          colorArr.push(color);
        }
      }
      if (portsArr.length === 0) continue;
      mqttControl.onPublishBatch(controllerId, deviceIds, {
        serial: controllerId,
        cmd: 'cs',
        type: 1,
        data: { ports: portsArr, onoff: onoffArr, bright: brightArr, color: colorArr },
      });
    }

    for (const u of updates) {
      store.commit('home/updateDevice', {
        groupId: u.groupId,
        id: u.id,
        data: { item: { onoff: isOn } },
      });
      Axios.patch(DeviceApi.UPDATE_DEVICE.replace(':id', u.id), {
        item: { onoff: isOn },
      }).catch(() => {});
    }

    return { synced: updates.length };
  } catch (e) {
    console.error('syncCurtainLinkedLights failed:', e);
    return { synced: 0 };
  }
};

/**
 * MQTTCurtainCommand → 연동 조명 turnOn (멈춤이면 null)
 */
export const linkedLightsTurnOnForCurtainCommand = (command) => {
  if (command === 'ISOPEN') return true;
  if (command === 'ISCLOSE') return false;
  return null;
};
